<template>
  <div class="tn-booking">
    <template v-if="loading">
      <v-skeleton-loader type="card-heading, list-item-two-line" class="mb-6"></v-skeleton-loader>
      <div class="tn-booking__grid">
        <v-skeleton-loader type="card, card"></v-skeleton-loader>
        <v-skeleton-loader type="card"></v-skeleton-loader>
      </div>
    </template>

    <v-card v-else-if="!booking" class="tn-empty py-12">
      <v-icon size="44">mdi-file-alert-outline</v-icon>
      <div>Couldn't get the booking details.</div>
      <v-btn text color="primary" :to="{ name: 'cruisesBookings' }">Back to cruise bookings</v-btn>
    </v-card>

    <template v-else>
      <v-alert v-if="!booking.cruise_details" type="info" text dense class="mb-5">
        Cabins, the route, the customer's account and the payment show up once the latest API update is deployed.
      </v-alert>

      <booking-summary
        :booking="booking"
        type="cruises"
        icon="mdi-ferry"
        :title="details.cruise.name || 'Cruise booking'"
        :tags="[stateLabel]"
        :total="booking.total_price"
        :total-label="details.state === 'inquiry' ? 'Quoted total' : 'Booking total'"
      >
        <template v-slot:actions>
          <v-btn
            v-if="details.state === 'inquiry'"
            depressed
            :outlined="!!activeLink"
            color="primary"
            small
            class="mt-2"
            :loading="sending"
            :disabled="!canSend"
            @click="confirmSendLink"
          >
            <v-icon left size="18">mdi-email-fast-outline</v-icon>
            {{ activeLink ? 'Send again' : 'Send payment link' }}
          </v-btn>
          <div v-if="details.state === 'inquiry' && sendBlockedReason" class="tn-muted mt-1">{{ sendBlockedReason }}</div>
          <div v-else-if="activeLink" class="tn-muted mt-1">Link sent {{ dateTime(activeLink.sent_at) }}</div>
        </template>
      </booking-summary>

      <div class="tn-booking__grid">
        <div class="tn-booking__col">
          <v-card class="tn-panel">
            <div class="tn-panel__head"><v-icon size="20">mdi-ferry</v-icon> Cruise & dates</div>
            <div class="tn-panel__body">
              <div class="tn-activity pt-0">
                <span class="tn-activity__thumb">
                  <v-icon size="26">mdi-ferry</v-icon>
                  <img v-if="details.cruise.image" :src="details.cruise.image" alt="" loading="lazy" @error="$event.target.style.display = 'none'">
                </span>
                <div class="tn-activity__body">
                  <div class="tn-activity__title">
                    {{ details.cruise.name || 'Cruise #' + details.cruise.id }}
                    <span v-if="details.cruise.deleted" class="tn-tag">Removed from the catalog</span>
                  </div>
                  <div class="tn-activity__meta">
                    <span v-if="details.cruise.from"><v-icon size="16">mdi-map-marker-path</v-icon>{{ details.cruise.from }}<template v-if="details.cruise.to"> → {{ details.cruise.to }}</template></span>
                    <span v-if="details.cruise.line"><v-icon size="16">mdi-domain</v-icon>{{ details.cruise.line }}</span>
                    <span v-if="details.cruise.ship"><v-icon size="16">mdi-sail-boat</v-icon>{{ details.cruise.ship }}</span>
                    <span v-if="details.cruise.stars"><v-icon size="16" color="amber darken-1">mdi-star</v-icon>{{ details.cruise.stars }} stars</span>
                  </div>
                </div>
              </div>

              <stay-strip
                class="mt-2"
                start-label="Embarkation"
                end-label="Disembarkation"
                :start="details.stay.start"
                :end="details.stay.end"
                :middle="details.stay.nights ? details.stay.nights + ' night' + (details.stay.nights === 1 ? '' : 's') : ''"
              />

              <dl class="tn-facts">
                <div><dt>Guests</dt><dd>{{ guestsLabel }}</dd></div>
                <div><dt>Status</dt><dd>{{ stateText }}</dd></div>
              </dl>
            </div>
          </v-card>

          <v-card v-if="booking.cruise_details" class="tn-panel">
            <div class="tn-panel__head"><v-icon size="20">mdi-map-marker-path</v-icon> Day by day</div>
            <div class="tn-panel__body">
              <p v-if="weekdayNote" class="tn-muted mt-n1 mb-3">{{ weekdayNote }}</p>
              <template v-if="program.length">
                <div v-for="(day, i) in program" :key="day.day" class="tn-day">
                  <div class="tn-day__when">Day {{ day.day }}<span v-if="day.date">{{ shortDate(day.date) }}</span></div>
                  <div class="tn-day__what">
                    <div v-if="day.title || dayTag(i)" class="tn-program__title">
                      <span v-if="day.title">{{ day.title }}</span>
                      <span v-if="dayTag(i)" class="tn-program__tag">{{ dayTag(i) }}</span>
                    </div>
                    <ul class="tn-program">
                      <li v-for="(line, j) in day.lines" :key="j">
                        <span v-if="day.timed" class="tn-program__time">{{ line.time }}</span>
                        <span>{{ line.text }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </template>
              <p v-else-if="details.overview" class="tn-program__overview mb-0">{{ details.overview }}</p>
              <p v-else class="tn-muted mb-0">
                {{ details.cruise.deleted ? 'The cruise was removed from the catalog, so its program is not available.' : 'The cruise description has no day-by-day program.' }}
              </p>
            </div>
          </v-card>

          <v-card class="tn-panel">
            <div class="tn-panel__head"><v-icon size="20">mdi-bed-king-outline</v-icon> Cabins</div>
            <v-simple-table v-if="details.cabins.length">
              <thead>
                <tr><th>Cabin</th><th>Guests</th><th class="text-right">Per night</th><th class="text-right">Total</th></tr>
              </thead>
              <tbody>
                <tr v-for="(cabin, i) in details.cabins" :key="i">
                  <td>
                    <div class="font-weight-medium">{{ cabin.cabin || 'Cabin no longer in the catalog' }}</div>
                    <div v-if="cabin.occupancy" class="tn-muted">{{ cabin.occupancy }}</div>
                  </td>
                  <td>{{ cabinGuests(cabin) }}</td>
                  <td class="text-right">{{ cabin.price_per_night !== null ? money(cabin.price_per_night) : '—' }}</td>
                  <td class="text-right font-weight-medium">{{ cabin.total !== null ? money(cabin.total) : '—' }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-else class="tn-panel__body"><p class="tn-muted mb-0">No cabin was saved with this booking.</p></div>
          </v-card>

          <travellers-panel :travellers="travellers" />
        </div>

        <div class="tn-booking__col">
          <customer-panel
            :customer="customer"
            :contact="contact"
            :empty-text="hasOverview ? 'No customer account is linked to this booking (guest inquiry).' : 'Available after the API update.'"
          />
          <payment-panel
            :payment="payment"
            :amount="booking.total_price"
            :empty-text="details.state === 'inquiry' ? 'Not paid yet: cruises are paid through the emailed payment link.' : 'No payment was recorded for this booking.'"
          />

          <v-card class="tn-panel">
            <div class="tn-panel__head"><v-icon size="20">mdi-email-fast-outline</v-icon> Payment links</div>
            <div class="tn-panel__body">
              <ul v-if="links.length" class="tn-attempts mt-0">
                <li v-for="link in links" :key="link.id">
                  <span class="tn-attempts__dot" :class="'is-' + linkTone(link)"></span>
                  <div class="tn-attempts__text">
                    <div class="tn-attempts__title">{{ linkLabel(link) }} · {{ money(link.amount, link.currency) }}</div>
                    <div class="tn-muted">
                      <template v-if="link.legacy">Opened from an old e-mail {{ dateTime(link.opened_at) }}</template>
                      <template v-else>Sent {{ dateTime(link.sent_at) }}<template v-if="link.sent_by"> by {{ link.sent_by }}</template> to {{ link.email || '—' }}</template>
                    </div>
                    <div v-if="link.opened_at && !link.legacy" class="tn-muted">Opened {{ dateTime(link.opened_at) }}</div>
                    <div v-if="link.paid_at" class="tn-muted">Paid {{ dateTime(link.paid_at) }}</div>
                  </div>
                </li>
              </ul>
              <p v-else class="tn-muted mb-0">{{ booking.cruise_details ? 'No payment link was sent yet.' : 'Available after the API update.' }}</p>
            </div>
          </v-card>
          <references-panel :items="references" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import BookingSummary from '../../components/booking/BookingSummary.vue'
import CustomerPanel from '../../components/booking/CustomerPanel.vue'
import PaymentPanel from '../../components/booking/PaymentPanel.vue'
import ReferencesPanel from '../../components/booking/ReferencesPanel.vue'
import StayStrip from '../../components/booking/StayStrip.vue'
import TravellersPanel from '../../components/booking/TravellersPanel.vue'
import bookingPage from '../../mixins/bookingPage'
import { headers, sendEmailCruise } from '../../links'

// "2025-06-28" + n days, as a local date (no time zone shift).
function addDays (isoDate, days) {
  const d = new Date(`${String(isoDate).slice(0, 10)}T00:00:00`)
  if (isNaN(d.getTime())) return null
  d.setDate(d.getDate() + days)
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-')
}

// A program line: "12:30 Lunch on board" / "PM: Explore Karnak" → time + text.
function programLine (item) {
  const match = /^(\d{1,2}[:.]\d{2}(?:\s*(?:noon|am|pm)\b)?|AM|PM)\s*[:\-–]?\s+(.+)$/i.exec(item)
  return match ? { time: match[1], text: match[2] } : { time: null, text: item }
}

// What an API without `cruise_details` (not deployed yet) already returns.
function fallbackDetails (b) {
  const c = b.cruise
  const status = String(b.status || '').toLowerCase()
  const nights = c ? Number(c.number_of_nights) : null
  return {
    cruise: { id: b.model_id, deleted: !c, name: c ? c.name : null, line: c ? c.cruise_line : null, ship: c ? c.ship_name : null, stars: c ? Number(c.stars) : null, image: c ? c.master_image : null, from: null, to: null },
    // the website stores no end date: embarkation + nights
    stay: { start: b.start_date, end: b.end_date || (b.start_date && nights ? addDays(b.start_date, nights) : null), nights },
    cabins: [],
    state: !status ? 'inquiry' : (/confirm|paid/.test(status) ? 'paid' : (/reject/.test(status) ? 'rejected' : status))
  }
}

export default {
  components: { BookingSummary, CustomerPanel, PaymentPanel, ReferencesPanel, StayStrip, TravellersPanel },
  mixins: [bookingPage],
  data () {
    return {
      sending: false
    }
  },
  computed: {
    details () {
      return this.booking.cruise_details || fallbackDetails(this.booking)
    },
    stateLabel () {
      // "Paid" would repeat the status chip; only the inquiry / rejected states get a tag.
      return { inquiry: 'Inquiry', rejected: 'Rejected' }[this.details.state] || null
    },
    stateText () {
      return {
        inquiry: 'Inquiry: waiting for the team to email a payment link',
        paid: 'Paid',
        rejected: 'Rejected'
      }[this.details.state] || this.details.state
    },
    // Day-by-day program from the cruise description (API: cruise_details.program), on the booked dates.
    program () {
      return (this.details.program || []).map(day => {
        const lines = (day.items || []).map(programLine)
        // a day with times keeps the column for its untimed lines too ("Overnight Luxor")
        return { ...day, lines, timed: lines.some(line => line.time) }
      })
    },
    weekdayNote () {
      const written = this.details.program_weekday
      const actual = this.details.stay.start ? this.weekday(`${String(this.details.stay.start).slice(0, 10)}T00:00:00`) : ''
      if (!written || !actual || written === actual) return ''
      return `The catalog program is written for a ${written} departure; this booking starts on a ${actual}, so the days may not match exactly.`
    },
    contactEmail () {
      return this.contact && this.contact.contact_email
    },
    // Links e-mailed by the team (API: cruise_details.payment_links, newest first).
    links () {
      return this.details.payment_links || []
    },
    activeLink () {
      return this.links.find(link => link.status === 'sent' && !link.legacy) || null
    },
    // Same refusals as the API (App\Services\Payments\PaymentLinks).
    sendBlockedReason () {
      if (!this.contactEmail) return 'No contact email on this booking'
      if (!(Number(this.booking.total_price) > 0)) return 'This booking has no price, so no link can be sent'
      return ''
    },
    canSend () {
      return !this.sendBlockedReason && !this.sending
    },
    references () {
      return [
        { label: 'Booking ID', value: String(this.booking.id) },
        { label: 'Cruise ID', value: this.details.cruise.id ? String(this.details.cruise.id) : null }
      ]
    }
  },
  methods: {
    shortDate (value) {
      const d = new Date(`${String(value).slice(0, 10)}T00:00:00`)
      return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
    },
    dayTag (index) {
      if (this.program.length < 2) return ''
      if (index === 0) return 'Embarkation'
      return index === this.program.length - 1 ? 'Disembarkation' : ''
    },
    cabinGuests (cabin) {
      if (cabin.adults === null || cabin.adults === undefined) return '—'
      const parts = [`${cabin.adults} adult${cabin.adults === 1 ? '' : 's'}`]
      if (cabin.children) parts.push(`${cabin.children} child${cabin.children === 1 ? '' : 'ren'}${cabin.child_ages && cabin.child_ages.length ? ' (' + cabin.child_ages.join(', ') + ')' : ''}`)
      return parts.join(', ')
    },
    linkLabel (link) {
      if (link.status === 'paid') return 'Paid'
      if (link.status === 'cancelled') return 'Replaced by a newer link'
      return link.opened_at ? 'Opened, not paid yet' : 'Sent, not opened yet'
    },
    linkTone (link) {
      if (link.status === 'paid') return 'success'
      if (link.status === 'cancelled') return 'grey'
      return 'warning'
    },
    confirmSendLink () {
      const replaced = this.activeLink ? `<br>The link sent ${this.dateTime(this.activeLink.sent_at)} will stop working.` : ''
      Swal.fire({
        title: this.activeLink ? 'Send a new payment link?' : 'Send the payment link?',
        html: `${this.contactEmail} will get an email with a Payfort link for <b>${this.money(this.booking.total_price)}</b>.${replaced}`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Send link',
        cancelButtonText: 'Not now'
      }).then(result => {
        if (result.isConfirmed) this.sendLink()
      })
    },
    sendLink () {
      this.sending = true
      const formData = new FormData()
      formData.append('book_id', this.booking.id)
      formData.append('price', this.booking.total_price)
      formData.append('status', 'accept')

      this.$http.post(sendEmailCruise, formData, { headers: headers(this.$cookies.get('userToken')) }).then(() => {
        this.sending = false
        this.notify('success', `Payment link sent to ${this.contactEmail}`)
        this.loadBooking(true)
      }, err => {
        this.sending = false
        this.notify('error', (err.body && err.body.message) || 'The email could not be sent')
      })
    }
  }
}
</script>
